import { DarkTheme, DefaultTheme, ThemeProvider, Stack, usePathname } from "expo-router";
import { useFonts } from "expo-font";
import { StatusBar } from "expo-status-bar";
import "react-native-reanimated";
import { useColorScheme } from "@/hooks/useColorScheme";
import Splash, { SplashScreenController } from "./splash";
import { useAuth } from "@components/auth/useAuth";
import { AuthProvider } from "@components/auth/AuthProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
import StoreProvider from "@/services/store/store-provider";
import "../global.css";

import { AlertRenderer, CustomAlert } from "@/components/ThemedAlert";
import { StyleSheet, View, ActivityIndicator, Platform, AppState, Linking, TouchableOpacity } from "react-native";
import { ThemedButton } from "@/components/ThemedButton";
import { useShareIntent } from "expo-share-intent";
import { File, Paths } from "expo-file-system";
import { useFirebase } from "@components/firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedText } from "@/components/ThemedText";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { uploadQueue } from "@/services/upload-queue";
import { initMemories } from "@services/initMemories";
import "@services/console-logger";
import { logAndFlush, flushQueue } from "@/services/batch-logger";
import { initAnalytics, trackPageView, trackPageDwellTime } from "@/services/analytics";
import { startTelemetryMonitor } from "@/services/telemetry-monitor";
import { initCrashReporter } from "@/services/crash-reporter";
import { HintBar } from "@/components/hints/HintBar";
import { promptStartupGpsPermissionIfNeeded } from "@/services/location-permissions";
import { RemoteConfigService } from "@/services/remote-config-service";
import { recordNavigation } from "@/services/navigation-history";
import SignIn from "./sign-in";

/**
 * Observes navigation route changes and dwell times across Expo Router pages
 * and dispatches telemetry to Google Analytics.
 */
function AnalyticsRouteTracker(): React.JSX.Element | null {
  const pathname = usePathname();
  const previousPathRef = useRef<string | null>(null);
  const pageStartTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    initAnalytics().catch((err) => console.warn("[Analytics] Init error:", err));
    startTelemetryMonitor(60000);
  }, []);

  useEffect(() => {
    if (!pathname) return;

    recordNavigation(pathname);

    // Report dwell duration for the previous page
    if (previousPathRef.current && previousPathRef.current !== pathname) {
      const elapsedSec = (Date.now() - pageStartTimeRef.current) / 1000;
      trackPageDwellTime(previousPathRef.current, elapsedSec);
    }

    previousPathRef.current = pathname;
    pageStartTimeRef.current = Date.now();
    trackPageView(pathname);
  }, [pathname]);

  return null;
}

export default function RootLayout() {
  initCrashReporter();
  initMemories().catch((err) => console.error("Failed to initialize Memories:", err));

  const colorScheme = useColorScheme();
  const [, fontError] = useFonts(
    Platform.OS === "web"
      ? {}
      : {
          SpaceMono: require("@assets/fonts/SpaceMono-Regular.ttf"),
        }
  );

  useEffect(() => {
    if (fontError) {
      console.warn("Font loading error:", fontError);
    }
  }, [fontError]);

  return (
    <ThemeProvider value={colorScheme === "dark" ? DarkTheme : DefaultTheme}>
      <AuthProvider>
        <StoreProvider>
          <AnalyticsRouteTracker />
          <SplashScreenController />

          <ErrorBoundary>
            <HintBar />
            <RootNavigator />
          </ErrorBoundary>
          <AlertRenderer />
        </StoreProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}


function RootNavigator() {
  const services = useFirebase();
  const { user, isAuthenticated, isInitialized } = useAuth();

  const { hasShareIntent, shareIntent, resetShareIntent, error: shareIntentError, isReady } = useShareIntent({
    debug: Platform.OS !== "web",
    resetOnBackground: false,
    disabled: Platform.OS === "web",
  });
  const processedIntentsRef = useRef<Set<string>>(new Set());
  const activeIntentSignatureRef = useRef<string | null>(null);
  const [isSharingIntent, setIsSharingIntent] = useState(false);
  const [shareFileName, setShareFileName] = useState("");
  const [shareProgress, setShareProgress] = useState("");
  const [isBackgroundActive, setIsBackgroundActive] = useState(false);
  const [activeQueueCount, setActiveQueueCount] = useState(0);

  const modalBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");

  /**
   * Reads any native Intent snapshot recorded on disk by MainActivity.
   * This provides immediate observability into whether Android delivered an intent to the native activity.
   */
  const checkNativeIntentLog = useCallback(async (): Promise<void> => {
    if (Platform.OS !== "android") return;
    try {
      const file = new File(Paths.cache, "last_share_intent.json");
      if (file.exists) {
        const content = await file.text();
        logAndFlush("info", `[ShareIntent] Native Intent snapshot on disk: ${content}`);
      } else {
        logAndFlush("info", `[ShareIntent] No native intent snapshot found at ${file.uri}`);
      }
    } catch (err: any) {
      logAndFlush("warn", `[ShareIntent] Error reading native intent snapshot: ${err?.message || err}`);
    }
  }, []);

  // Observability: Log RootNavigator mount and inspect disk snapshot
  useEffect(() => {
    logAndFlush("info", `[ShareIntent] RootNavigator mounted. Platform=${Platform.OS}, initial AppState=${AppState.currentState}`);
    checkNativeIntentLog().catch(() => {});
  }, [checkNativeIntentLog]);

  // Initialize Firebase Remote Config service
  useEffect(() => {
    if (services?.app) {
      RemoteConfigService.getInstance().initialize(services.app).catch((err) => {
        console.warn("[RemoteConfig] Startup initialization error:", err);
      });
    }
  }, [services?.app]);

  // Synchronize active user Beta Group with RemoteConfigService for conditional features
  useEffect(() => {
    RemoteConfigService.getInstance().setUserBetaGroup(user?.betaGroup);
  }, [user?.betaGroup]);

  // Prompt user on startup for GPS permissions if not already granted or declined
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) {
      return;
    }
    const timer = setTimeout(() => {
      promptStartupGpsPermissionIfNeeded().catch((err) => {
        console.warn("[Location] Startup GPS prompt check failed:", err);
      });
    }, 1200);
    return () => clearTimeout(timer);
  }, [isInitialized, isAuthenticated]);

  // Observability: Monitor AppState transitions
  useEffect(() => {
    const sub = AppState.addEventListener("change", (nextState) => {
      logAndFlush("info", `[ShareIntent] AppState -> "${nextState}" (Platform: ${Platform.OS})`);
      if (nextState === "active") {
        checkNativeIntentLog().catch(() => {});
        // Auto-resume any uploads interrupted while the phone was suspended
        if (services && uploadQueue.getActiveItemsCount() > 0 && !uploadQueue.getIsProcessing()) {
          logAndFlush("info", "[UploadQueue] AppState active: auto-resuming interrupted uploads.");
          uploadQueue.processQueue(services, { isBackground: false }).catch(() => {});
        }
      }
    });
    return () => sub.remove();
  }, [checkNativeIntentLog, services]);

  // Observability: Monitor initial linking URL and incoming URL events
  useEffect(() => {
    Linking.getInitialURL()
      .then((url) => {
        if (url) {
          logAndFlush("info", `[ShareIntent] Linking initial URL: ${url}`);
        }
      })
      .catch((err) => {
        logAndFlush("warn", `[ShareIntent] Linking.getInitialURL failed: ${err?.message || err}`);
      });

    const urlSub = Linking.addEventListener("url", (event) => {
      logAndFlush("info", `[ShareIntent] Incoming Linking URL: ${event.url}`);
    });
    return () => urlSub.remove();
  }, []);

  const isMountedRef = useRef<boolean>(true);
  const resetShareIntentRef = useRef(resetShareIntent);
  resetShareIntentRef.current = resetShareIntent;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Subscribe to uploadQueue events for real-time progress and completion handling
  useEffect(() => {
    const unsubscribe = uploadQueue.subscribe((event) => {
      const activeCount = uploadQueue.getActiveItemsCount();
      setActiveQueueCount(activeCount);
      setIsBackgroundActive(uploadQueue.getIsBackgroundMode() && activeCount > 0);

      switch (event.type) {
        case "progress":
          if (event.item) {
            setShareFileName(event.item.name || (event.item as any).fileName || "");
          }
          if (event.currentIndex && event.totalItems) {
            setShareProgress(`Uploading ${event.currentIndex} of ${event.totalItems}...`);
          }
          break;

        case "completed":
          setIsSharingIntent(false);
          setIsBackgroundActive(false);

          if (event.failedCount && event.failedCount > 0) {
            CustomAlert.alert(
              "Upload Incomplete",
              `${event.failedCount} item(s) failed to upload: ${event.lastError || "Network or server error"}.\nWould you like to retry?`,
              [
                {
                  text: "Cancel",
                  style: "cancel",
                  onPress: () => {
                    uploadQueue.dismissFailedItems().catch(() => {});
                  },
                },
                {
                  text: "Retry Upload",
                  onPress: () => {
                    if (services) {
                      setIsSharingIntent(true);
                      uploadQueue.setBackgroundMode(false);
                      uploadQueue.retryFailedItems(services).catch(() => {});
                    }
                  },
                },
              ]
            );
          } else if (event.uploadedCount && event.uploadedCount > 0) {
            if (!event.isBackground) {
              const msg =
                event.uploadedCount > 1
                  ? `All ${event.uploadedCount} shared items saved to your gallery!`
                  : "Shared media saved to your gallery!";
              CustomAlert.alert("Success", msg);
            }
          } else if (event.duplicateCount && event.duplicateCount > 0) {
            if (!event.isBackground) {
              CustomAlert.alert("Notice", "Shared item(s) were already in your gallery.");
            }
          }
          break;

        case "cancelled":
          setIsSharingIntent(false);
          setIsBackgroundActive(false);
          break;
      }
    });

    return () => unsubscribe();
  }, [services]);

  // Check and resume unfinished upload queue on startup / auth readiness
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !services) return;

    uploadQueue
      .init()
      .then(() => {
        const activeCount = uploadQueue.getActiveItemsCount();
        const failedCount = uploadQueue.getFailedItemsCount();

        if (activeCount > 0 && !uploadQueue.getIsProcessing()) {
          logAndFlush("info", `[UploadQueue] Found ${activeCount} pending item(s) from previous session. Resuming in background...`);
          setIsBackgroundActive(true);
          uploadQueue.processQueue(services, { isBackground: true });
        } else if (failedCount > 0 && !uploadQueue.getIsProcessing()) {
          logAndFlush("warn", `[UploadQueue] Found ${failedCount} failed item(s) from previous session.`);
          CustomAlert.alert(
            "Unfinished Uploads",
            `You have ${failedCount} upload(s) from a previous session that could not complete. Would you like to retry?`,
            [
              {
                text: "Dismiss",
                style: "cancel",
                onPress: () => uploadQueue.dismissFailedItems().catch(() => {}),
              },
              {
                text: "Retry Upload",
                onPress: () => {
                  setIsSharingIntent(true);
                  uploadQueue.setBackgroundMode(false);
                  uploadQueue.retryFailedItems(services).catch(() => {});
                },
              },
            ]
          );
        }
      })
      .catch((err) => {
        console.warn("[UploadQueue] Error initializing queue on startup:", err);
      });
  }, [isInitialized, isAuthenticated, services]);

  // Observability: Track every change in useShareIntent hook outputs
  useEffect(() => {
    const rawFiles = (shareIntent?.files || []).filter(
      (f: any) => f && typeof f === "object" && !Array.isArray(f)
    );
    logAndFlush(
      "info",
      `[ShareIntent] Hook update: hasShareIntent=${hasShareIntent}, isReady=${isReady}, error=${shareIntentError || "none"}, type=${shareIntent?.type || "none"}, count=${rawFiles.length}, text=${shareIntent?.text ? "yes" : "no"}, webUrl=${shareIntent?.webUrl ? "yes" : "no"}`
    );
    if (!hasShareIntent && (shareIntent?.type || rawFiles.length > 0 || shareIntent?.text || shareIntent?.webUrl)) {
      logAndFlush(
        "info",
        `[ShareIntent] hasShareIntent is false but raw shareIntent has payload: ${JSON.stringify(shareIntent)}`
      );
    }
  }, [hasShareIntent, shareIntent, shareIntentError, isReady]);

  /**
   * Cancels the active upload queue and closes the progress modal.
   */
  const handleCancelShareIntent = () => {
    console.log("[ShareIntent] User cancelled share import request.");
    uploadQueue.cancelQueue().catch(() => {});
    const activeSig = activeIntentSignatureRef.current;
    if (activeSig) {
      processedIntentsRef.current.add(activeSig);
    }
    setIsSharingIntent(false);
    resetShareIntentRef.current(true);
    logAndFlush("info", "[ShareIntent] User cancelled and closed the share import request.");
  };

  /**
   * Hides the progress modal so the upload continues silently in the background.
   */
  const handleUploadInBackground = () => {
    console.log("[ShareIntent] User selected 'Upload in background'.");
    uploadQueue.setBackgroundMode(true);
    setIsSharingIntent(false);
    setIsBackgroundActive(true);
    logAndFlush("info", "[ShareIntent] Progress form hidden; upload continues in background.");
  };

  /**
   * Restores the foreground progress modal from background mode ("Upload Now").
   */
  const handleUploadNow = () => {
    console.log("[ShareIntent] User selected 'Upload Now' / foreground view.");
    uploadQueue.setBackgroundMode(false);
    setIsSharingIntent(true);
    setIsBackgroundActive(false);
  };

  useEffect(() => {
    if (shareIntentError) {
      console.error("[ShareIntent] Native module error:", shareIntentError);
      logAndFlush("error", `[ShareIntent] Native module error: ${shareIntentError}`);
    }

    if (!hasShareIntent || !shareIntent) {
      return;
    }

    // Filter out any non-object items (such as Kotlin Pair tuples serialized into the files array)
    const rawFiles = (shareIntent.files || []).filter(
      (f: any) => f && typeof f === "object" && !Array.isArray(f)
    );
    const fileKeys = rawFiles
      .map((f: any) => `${f.fileName || ""}:${f.path || f.filePath || f.contentUri || ""}:${f.size || f.fileSize || ""}`)
      .sort()
      .join(";");
    const intentSignature = `${shareIntent.type || ""}|${fileKeys}|${shareIntent.text || ""}|${shareIntent.webUrl || ""}`;

    const processIntent = async () => {
      // 1. In-memory check: prevent duplicate triggers within the active app session
      if (processedIntentsRef.current.has(intentSignature)) {
        console.log("[ShareIntent] Intent signature already processed in this session, ignoring duplicate trigger:", intentSignature);
        resetShareIntentRef.current(true);
        return;
      }

      if (!isMountedRef.current) return;

      if (uploadQueue.getIsProcessing()) {
        console.log("[ShareIntent] Upload already in progress, enqueuing additional files.");
      }

      const filesSummary = rawFiles.map((f: any, idx: number) => ({
        index: idx,
        fileName: f.fileName,
        mimeType: f.mimeType,
        path: f.path,
        filePath: f.filePath,
        contentUri: f.contentUri,
        size: f.size || f.fileSize,
      }));

      console.log("[ShareIntent] Intent detected:", {
        hasShareIntent,
        type: shareIntent.type,
        text: shareIntent.text,
        webUrl: shareIntent.webUrl,
        filesCount: rawFiles.length,
        filesSummary,
        isInitialized,
        isAuthenticated,
        hasServices: !!services,
      });
      logAndFlush(
        "info",
        `[ShareIntent] Intent detected: type=${shareIntent.type}, count=${rawFiles.length}, authInit=${isInitialized}, auth=${isAuthenticated}`
      );

      // Wait until auth state is initialized and Firebase services are ready
      if (!isInitialized || !services) {
        console.log("[ShareIntent] Waiting for auth and Firebase initialization...");
        return;
      }

      if (!isAuthenticated) {
        console.warn("[ShareIntent] User is not authenticated. Aborting share intent.");
        logAndFlush("warn", "[ShareIntent] User not authenticated when receiving share intent.");
        CustomAlert.alert("Authentication Required", "Please log in first to save shared media.");
        resetShareIntentRef.current(true);
        return;
      }

      // Resolve and normalize paths across path, filePath, and contentUri (standard on Android)
      const validFiles = rawFiles
        .map((f: any) => {
          let resolvedPath =
            f.path ||
            (f.filePath ? (f.filePath.startsWith("file://") ? f.filePath : `file://${f.filePath}`) : null) ||
            f.contentUri ||
            null;
          if (resolvedPath && resolvedPath.startsWith("/") && !resolvedPath.startsWith("file://")) {
            resolvedPath = `file://${resolvedPath}`;
          }
          return {
            ...f,
            path: resolvedPath,
          };
        })
        .filter((f: any) => !!f.path);

      if (validFiles.length === 0) {
        console.warn("[ShareIntent] No valid file paths found in share intent payload:", JSON.stringify(shareIntent));
        logAndFlush("warn", `[ShareIntent] No valid files found in intent payload. Raw: ${JSON.stringify(shareIntent)}`);
        CustomAlert.alert("Shared Media", "No readable media files were found in the shared intent.");
        processedIntentsRef.current.add(intentSignature);
        resetShareIntentRef.current(true);
        return;
      }

      // Mark intent as processed in-memory to prevent concurrent triggers in active session
      processedIntentsRef.current.add(intentSignature);
      activeIntentSignatureRef.current = intentSignature;
      resetShareIntentRef.current(true);

      setShareFileName(validFiles[0].fileName || "Shared media");
      setShareProgress(`Preparing ${validFiles.length} item(s)...`);
      setIsSharingIntent(true);
      logAndFlush("info", `[ShareIntent] Enqueuing ${validFiles.length} item(s) to persistent queue...`);

      try {
        await uploadQueue.enqueueFiles(validFiles);
        // Start processing queue (foreground by default, user can click 'Upload in background')
        uploadQueue.processQueue(services, { isBackground: false });
      } catch (queueErr: any) {
        console.error("[ShareIntent] Failed to enqueue files:", queueErr);
        logAndFlush("error", `[ShareIntent] Enqueue error: ${queueErr?.message || queueErr}`);
        CustomAlert.alert("Error", "Failed to queue shared media for upload.");
        setIsSharingIntent(false);
      } finally {
        flushQueue().catch(() => {});
      }
    };

    processIntent();
  }, [hasShareIntent, shareIntent, isInitialized, isAuthenticated, services, shareIntentError]);

  if (!isInitialized) {
    // Show our own splash screen - do we also need the Router Splashscreen?
    return <Splash />;
  }

  if (!isAuthenticated) {
    return <SignIn />;
  }

  // See: Protected Routes - https://docs.expo.dev/router/advanced/protected/
  console.log("User", user, isAuthenticated);

  return (
    <>
      <Stack screenOptions={{ headerShown: false }}>
        {/* If we are authenticated */}
        <Stack.Protected guard={isInitialized && isAuthenticated}>
          <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
          <Stack.Screen name="(z_shared)" options={{ headerShown: false }} />
        </Stack.Protected>
        <Stack.Screen name="+not-found" options={{ title: "Not Found", headerShown: false }} />
      </Stack>

      <StatusBar style="auto" />

      {/* Floating indicator when upload is actively processing in the background */}
      {isBackgroundActive && !isSharingIntent && (
        <TouchableOpacity
          style={styles.bgBanner}
          onPress={handleUploadNow}
          accessibilityLabel="View background upload progress"
          activeOpacity={0.85}
        >
          <ActivityIndicator size="small" color="#FFFFFF" style={{ marginRight: 8 }} />
          <ThemedText style={styles.bgBannerText} numberOfLines={1}>
            Uploading in background ({activeQueueCount} remaining)
          </ThemedText>
          <ThemedText style={styles.bgBannerAction}>Upload Now</ThemedText>
        </TouchableOpacity>
      )}

      {isSharingIntent && (
        <View style={styles.shareOverlay}>
          <View style={[styles.shareModal, { backgroundColor: modalBg }]}>
            <ActivityIndicator size="large" color="#4A90E2" style={{ marginBottom: 16 }} />
            <ThemedText type="subtitle" style={[styles.shareTitle, { color: textColor }]}>
              Importing Shared Media
            </ThemedText>
            <ThemedText style={[styles.shareFileName, { color: textColor }]} numberOfLines={1}>
              {shareFileName}
            </ThemedText>
            <ThemedText style={styles.shareProgress}>{shareProgress}</ThemedText>
            <View style={styles.shareBtnColumn}>
              <ThemedButton
                title="Upload in Background"
                variant="primary"
                onPress={handleUploadInBackground}
                style={styles.modalBtn}
                accessibilityLabel="Upload in Background"
              />
              <ThemedButton
                title="Cancel Import"
                variant="minimal"
                onPress={handleCancelShareIntent}
                style={[styles.modalBtn, { marginTop: 8 }]}
                accessibilityLabel="Cancel Import"
              />
            </View>
          </View>
        </View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  shareOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  shareModal: {
    padding: 24,
    borderRadius: 20,
    width: "80%",
    maxWidth: 340,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  shareTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 8,
    textAlign: "center",
  },
  shareFileName: {
    fontSize: 14,
    opacity: 0.6,
    marginBottom: 12,
    textAlign: "center",
  },
  shareProgress: {
    fontSize: 14,
    color: "#4A90E2",
    fontWeight: "600",
    textAlign: "center",
  },
  shareBtnColumn: {
    width: "100%",
    marginTop: 18,
  },
  modalBtn: {
    width: "100%",
  },
  bgBanner: {
    position: "absolute",
    top: Platform.OS === "ios" ? 54 : 36,
    left: 16,
    right: 16,
    backgroundColor: "#1E293B",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 8,
    zIndex: 9998,
  },
  bgBannerText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
    flex: 1,
  },
  bgBannerAction: {
    color: "#38BDF8",
    fontSize: 12,
    fontWeight: "700",
    marginLeft: 8,
  },
});
